import type { AirQualityPoint, FallbackTier, IncidentFeature, WeatherSnapshot } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";
import { aqiColor, aqiBand } from "../lib/aqi";

interface Props {
  cityReports: IncidentFeature[];
  iticEvents: IncidentFeature[];
  airQuality: AirQualityPoint[];
  weather: WeatherSnapshot[];
  ageMinutes?: number;
  /** Honest tier for Traffy — empty scenario must not read as CLEAR. */
  reportsTier?: FallbackTier | "loading";
  iticTier?: FallbackTier | "loading";
  aqTier?: FallbackTier | "loading";
  weatherTier?: FallbackTier | "loading";
}

export function KpiStrip({
  cityReports,
  iticEvents,
  airQuality,
  weather,
  ageMinutes,
  reportsTier,
  iticTier,
  aqTier,
  weatherTier,
}: Props) {
  const openReports = cityReports.filter((r) => r.status !== "resolved").length;
  const totalEvents = iticEvents.length;
  const aq = airQuality[0];
  const w = weather[0];
  const traffyLive = reportsTier === "live";
  const iticLive = iticTier === "live" || iticTier === undefined;
  const reportsUnknown = !traffyLive && openReports === 0 && reportsTier !== "loading";

  const isLoading = reportsTier === "loading" || iticTier === "loading" || aqTier === "loading" || weatherTier === "loading";

  return (
    <>
      <PanelHeader title="ชีพจรเมือง " ageMinutes={ageMinutes} source="traffy·itic·air4thai·openmeteo" />
      <div className="kpi-grid" aria-busy={isLoading || undefined}>
        <div className="kpi" role="status" aria-label={`Citizen reports: ${openReports} open`}>
          <div className="label">TRAFFY:CR</div>
          <div
            className="value"
            style={{
              color: reportsUnknown
                ? "var(--text-3)"
                : openReports > 20 ? "var(--bad)"
                : openReports > 5 ? "var(--warn)"
                : openReports > 0 ? "var(--text)"
                : "var(--good)",
            }}
          >
            {reportsUnknown ? "—" : openReports}
            <span className="kpi-status-word">
              {reportsUnknown
                ? "OFFLINE"
                : openReports > 20 ? "CRITICAL"
                : openReports > 5 ? "ELEVATED"
                : openReports > 0 ? "OPEN"
                : "CLEAR"}
            </span>
          </div>
          <div className="sub">
            {reportsUnknown
              ? "TRAFFY UNAVAILABLE"
              : `${cityReports.length} TOTAL // OPEN`}
          </div>
        </div>

        <div className="kpi" role="status" aria-label={`Traffic events: ${totalEvents}`}>
          <div className="label">iTIC:EVT</div>
          <div
            className="value"
            style={{
              color: !iticLive && totalEvents === 0
                ? "var(--text-3)"
                : totalEvents > 15 ? "var(--bad)"
                : totalEvents > 5 ? "var(--warn)"
                : "var(--text)",
            }}
          >
            {!iticLive && totalEvents === 0 ? "—" : totalEvents}
            <span className="kpi-status-word">
              {!iticLive && totalEvents === 0
                ? "OFFLINE"
                : totalEvents > 15 ? "HIGH"
                : totalEvents > 5 ? "ELEVATED"
                : totalEvents > 0 ? "ACTIVE"
                : "QUIET"}
            </span>
          </div>
          <div className="sub">MUNICIPAL AREA</div>
        </div>

        <div
          className="kpi"
          role="status"
          aria-label={aq?.aqi != null ? `AQI ${aq.aqi}, ${aqiBand(aq.aqi)}` : "AQI unavailable"}
        >
          <div className="label">PM2.5:AQI</div>
          <div className="value" style={{ color: aq?.aqi != null ? aqiColor(aq.aqi) : "var(--text-3)" }}>
            {aq?.aqi ?? "—"}
            {aq?.aqi != null && (
              <span className="kpi-status-word">{aqiBand(aq.aqi)}</span>
            )}
          </div>
          <div className="sub">
            {aq?.pm25 != null
              ? `${aq.pm25.toFixed(1)} µg/m³`
              : aqTier === "scenario" || aqTier === "unavailable"
                ? "NO STATION"
                : "—"}
          </div>
        </div>

        <div
          className="kpi"
          role="status"
          aria-label={w?.tempC != null ? `Temperature ${Math.round(w.tempC)} degrees` : "Temperature unavailable"}
        >
          <div className="label">TEMP:WX</div>
          <div className="value">{w?.tempC != null ? `${Math.round(w.tempC)}°` : "—"}</div>
          <div className="sub">
            {w
              ? `FL ${Math.round((w.feelsLikeC ?? w.tempC) ?? 0)}° // ${(w.windKmh ?? 0).toFixed(0)} KMH`
              : weatherTier === "scenario" || weatherTier === "unavailable"
                ? "WX OFFLINE"
                : "—"}
          </div>
        </div>
      </div>
    </>
  );
}
